import { JSX, ReactNode, SyntheticEvent, useState } from 'react';
import { Box, Tab, Tabs } from '@mui/material';

/**
 * The dashboard's tab bar.
 *
 * Two behaviours are the whole point of this component, and both are easy to lose in a rewrite:
 *
 * 1. A panel is mounted on first visit and then stays mounted, hidden. Unmounting on every switch
 *    would make all six widgets refetch each time a tab is clicked, and mounting every panel up
 *    front would defeat the reason for splitting them at all.
 * 2. The selection is remembered, so coming back to the dashboard lands where you left it.
 */

export type DashboardTab = {
    /** Stable identifier. Used for the stored selection and the accessibility ids, not the label,
     *  so renaming a tab does not silently reset everyone's stored choice. */
    id: string,
    label: string,
    content: ReactNode,
}

type DashboardTabsProps = {
    tabs: DashboardTab[],
    /** localStorage key holding the last selected tab id. */
    storageKey: string,
}

/**
 * Storage is wrapped because it genuinely throws rather than returning null in a browser set to
 * block site data, which would take the whole dashboard down with it.
 */
const readStoredTab: (key: string) => string | null = (key: string): string | null => {
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
}

const writeStoredTab: (key: string, value: string) => void = (key: string, value: string): void => {
    try {
        window.localStorage.setItem(key, value);
    } catch {
        // A remembered tab is a convenience, never a reason to fail.
    }
}

export const DashboardTabs: (props: DashboardTabsProps) => JSX.Element = (
    { tabs, storageKey }: DashboardTabsProps
): JSX.Element => {
    // A stored id that no longer matches a tab falls back to the first one rather than leaving
    // the bar with nothing selected and every panel hidden.
    const initial: string = (() => {
        const stored: string | null = readStoredTab(storageKey);
        const known: boolean = tabs.some((tab: DashboardTab): boolean => tab.id === stored);
        return (stored !== null && known) ? stored : tabs[0].id;
    })();

    const [active, setActive] = useState<string>(initial);
    const [visited, setVisited] = useState<string[]>([initial]);

    function handleChange(_event: SyntheticEvent, value: string): void {
        setActive(value);
        setVisited((current: string[]): string[] => {
            return current.includes(value) ? current : [...current, value];
        });
        writeStoredTab(storageKey, value);
    }

    return (
        <Box sx={ { width: '100%' } }>
            <Box sx={ { borderBottom: 1, borderColor: 'divider' } }>
                <Tabs value={ active } onChange={ handleChange } aria-label="Dashboard sections">
                    { tabs.map((tab: DashboardTab): JSX.Element => {
                        return (
                            <Tab
                                key={ tab.id }
                                value={ tab.id }
                                label={ tab.label }
                                id={ "dashboard-tab-" + tab.id }
                                aria-controls={ "dashboard-panel-" + tab.id }
                                sx={ { textTransform: 'none', fontWeight: 600, fontSize: 14 } }
                            />
                        );
                    }) }
                </Tabs>
            </Box>
            { tabs.map((tab: DashboardTab): JSX.Element | null => {
                if (!visited.includes(tab.id)) {
                    return null;
                }
                const isActive: boolean = tab.id === active;

                // Hidden rather than unmounted: the panel keeps its data, its scroll position and
                // its open rows while another tab is in front of it.
                //
                // NOT the `hidden` attribute, and not `display: none`. Both collapse the panel to
                // zero width, and MUI's charts measure their container: at zero they subtract
                // their own margins and emit `<rect width="-30">`, which the browser rejects with
                // a console error every time the usage tab is switched away from. Collapsing the
                // height instead leaves the width measurement stable, and `visibility` is what
                // takes the panel out of the page for both painting and assistive technology.
                return (
                    <Box
                        key={ tab.id }
                        role="tabpanel"
                        aria-hidden={ !isActive }
                        id={ "dashboard-panel-" + tab.id }
                        aria-labelledby={ "dashboard-tab-" + tab.id }
                        sx={ isActive
                            ? { pt: 2 }
                            : { pt: 2, height: 0, overflow: 'hidden', visibility: 'hidden' }
                        }
                    >
                        { tab.content }
                    </Box>
                );
            }) }
        </Box>
    );
}
